import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from 'prop-types';

import { Column, usePagination, useSortBy, useTable } from 'react-table';

import TableCSVExport from '../../../component/TableCSVExport';

// Context.
import { InstanaIntegrationContext } from '../../../context/InstanaIntegrationContext';
import { FilterContext } from '../../../context/FilterContext';

// Helper.
import Helper from '../../../helpers/Helper';
import { DatabaseLabel, DatabasesLabel, getDefaultPageSize } from '../../../helpers/utils';

// Types.
import InstanceTarget from '../../../types/instance/InstanceTarget';
import InstanaService from "../../../types/integration/InstanaService";
import DatabasesDataTable from '../../../types/instance/tables/DatabasesDataTable';

// Components.
import Alert from '../../Alert';
import GenericBreakdown from '../../GenericBreakdown';
import InstanaDatabaseButton from '../../buttons/InstanaDatabaseButton';
import ReactTable from '../../ReactTable';

// Constants and enums.
import { DATA_INITIALISING, DATA_LOADING, DATA_LOADED } from "../../Constants";
import { FilterType } from "../../../typescript/Enums";
import { useLocation, useNavigate } from "react-router-dom";
import SearchInput from '../../SearchInput';

function DatabasesTable(props: { instance: InstanceTarget, batchId?: string, statementId?: string, instanceTime: number, databasesDataTable: DatabasesDataTable[], setFilterOptions: Function, currentTab?: string, tabIdPrefix?: string }) {
    const tabIdPrefix = props.tabIdPrefix || '';
    const { instanaIntegrations } = useContext(InstanaIntegrationContext);
    const { currentFilterOptions } = useContext(FilterContext);
    const [loading, setLoading] = useState<number>(DATA_INITIALISING);
    const [instanaServices, setInstanaServices] = useState<InstanaService[]>([]);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus({ preventScroll: true });
        }
    }, []);

    const navigate = useNavigate();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const [tableFilter, setTableFilter] = useState(tabIdPrefix ? "" : (query.get("filter") || ""));
    const initialPageIndex = Number(query.get("page") || 0);
    const initialPageSize = Number(query.get("pageSize") || getDefaultPageSize());

    const {
        instance, instanceTime, setFilterOptions
    } = props;

    function clearTableFilter() {

        // Clear any pre-existing filter values.
        setTableFilter('');
    }

    useEffect(() => {
        // Clear Filter if tab is changed
        if (props.currentTab) {
            setTableFilter('')
        }
    }, [props.currentTab]);

    useEffect(() => {
        const getInstanaIntegration = async () => {
            const discoveredInstanaServices: InstanaService[] = await Helper.getInstanaDatabaseIntegrations(instanaIntegrations, props.databasesDataTable, instance);
            setInstanaServices(discoveredInstanaServices);
        }

        // Get the batches for the selected instance and period.
        void getInstanaIntegration();

        // Mark the data as loaded.
        setLoading(DATA_LOADED);

    }, [instance, instanceTime, props.batchId, props.statementId, instanaIntegrations, props.databasesDataTable]);

    const data = useMemo(() => props.databasesDataTable.filter(row => (row.database.toLowerCase().includes(tableFilter.toLowerCase()))), [props.databasesDataTable, tableFilter]);

    const columns: Column[] = useMemo(() => {

        function filterData(filter: string) {

            // Build the updated filter options.
            const updatedFilterOptions = Helper.updateFilterOptions(FilterType.Databases, filter, currentFilterOptions);

            // Update the filters - this will trigger propogation of any filter changes.
            setFilterOptions(updatedFilterOptions);
        }


        return (
            [
                {
                    accessor: 'color',
                    disableSortBy: true
                },
                {
                    accessor: 'id',
                    disableSortBy: true
                },
                {
                    Header: DatabaseLabel(instance.type),
                    accessor: 'database',
                    className: 'fw-bold',
                    defaultCanSort: true,
                    sortType: 'alphanumeric',
                    Cell: ({ cell }) => (
                        <React.Fragment>
                            <span className="me-1 ps-1" style={{ backgroundColor: cell.row.values.color }}>&nbsp;</span>
                            {(cell.row.values.database !== '-') ? (
                                <React.Fragment>
                                    <button key={cell.column.id} className="btn btn-sm btn-link btn-filter"
                                        onClick={() => filterData(cell.row.values.database)}>{cell.row.values.database}</button>
                                    {instanaServices.filter(instana => instana.databaseName.toLowerCase() === cell.row.values.database.toLowerCase()).map(instana => (
                                        <InstanaDatabaseButton key={instana.instanaServiceId}
                                            instanaServiceUrl={instana.instanaServiceUrl} />
                                    ))}
                                </React.Fragment>
                            ) : (
                                <React.Fragment>-</React.Fragment>
                            )}
                        </React.Fragment>
                    ),
                },
                {
                    Header: 'Total Time',
                    accessor: 'breakdown',
                    className: 'generic-waits text-nowrap',
                    disableSortBy: true,
                    Cell: ({ cell }) => (
                        cell.row.values.waittime !== null ? (
                            cell.row.values.id < 20 ? (
                                <GenericBreakdown individualTime={cell.row.values.waittime} totalTime={instanceTime}
                                    color={((cell.row.values.color !== '') ? cell.row.values.color : '#999')}
                                    tooltip={cell.row.values.database} />
                            ) : (
                                <React.Fragment>-</React.Fragment>
                            )
                        ) : (
                            <React.Fragment>-</React.Fragment>
                        )
                    )
                },
                {
                    Header: 'Wait Time',
                    accessor: 'waittime',
                    headerClassName: 'text-end',
                    className: 'text-end',
                    defaultCanSort: true,

                    sortType: (rowA, rowB, columnId) => {
                        const aWait = Number(rowA.values[columnId]) || 0;
                        const bWait = Number(rowB.values[columnId]) || 0;

                        if (aWait !== bWait) {
                            return bWait - aWait;
                        }

                        const aDB = rowA.values['database'] || '';
                        const bDB = rowB.values['database'] || '';
                        return aDB.localeCompare(bDB);
                    },
                    Cell: ({ cell }) => (
                        cell.row.values.waittime !== null ? (
                            Helper.getTimeInEnglish(cell.row.values.waittime)
                        ) : (
                            <React.Fragment>-</React.Fragment>
                        )
                    )
                },
                {
                    Header: 'Weight %',
                    accessor: 'weight',
                    headerClassName: 'text-end',
                    className: 'text-end',
                    defaultCanSort: true,
                    sortType: 'basic',
                    Cell: ({ cell }) => {
                        const { waittime } = cell.row.values;
                        const percentage = (100 / instanceTime) * waittime;

                        return (
                            <React.Fragment>
                                {instanceTime ? `${Helper.formatPercentage(percentage)}%` : '-'}
                            </React.Fragment>
                        );
                    }
                }]
        )

    }, [instance.type, instanceTime, setFilterOptions, instanaServices, currentFilterOptions]);

    const tableInstance = useTable({
        columns,
        data,
        initialState: {
            hiddenColumns: ['id', 'color'],
            pageIndex: initialPageIndex,
            pageSize: initialPageSize,
        },
        autoResetPage: false,
        autoResetSortBy: false,
        autoResetFilters: false,
        autoResetSelectedRows: false,
        autoResetExpanded: false,
        autoResetGroupBy: false,
    },
        useSortBy, usePagination);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = tableInstance;

    useEffect(() => {
        // Don't update URL params when in panel mode (tabIdPrefix is set)
        if (tabIdPrefix) return;
        
        const params = new URLSearchParams(location.search);

        if (tableFilter) {
            params.set('filter', tableFilter);
        } else {
            params.delete('filter');
        }

        params.set('page', pageIndex.toString());
        params.set('pageSize', pageSize.toString());

        navigate({ search: params.toString() }, { replace: true });
    }, [tableFilter, pageIndex, pageSize, tabIdPrefix]);

    useEffect(() => {
        if (tableFilter) {
            gotoPage(0);
        }
    }, [tableFilter]);

    return (
        <div id={`${tabIdPrefix}databases`} className="tab-pane fade show" role="tabpanel" aria-labelledby={`${tabIdPrefix}databases-tab`}>
            <div className="card">
                <div className="card-header">
                    <i className="fal fa-database fa-fw" aria-hidden="true"></i>
                    {DatabasesLabel(instance.type)}
                    <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                        data-bs-target="#collapseDatabases" aria-expanded="false" aria-controls="collapseDatabases"></i>
                </div>
                <div id="collapseDatabases" className="card-body collapse show">
                    {Number(process.env.REACT_APP_API_LIMIT) - 1 === data.length - 1 && (
                        <Alert
                            message={`To help preserve performance, we limit the total number of database/schema records below to the top ${Number(process.env.REACT_APP_API_LIMIT) - 1} in descending order by their total wait time.`}
                            heading={DatabaseLabel(instance.type)} variant="alert-info" />
                    )}
                    <div className="row row-cols-1 row-cols-md-2 table-search">
                        <div className="col col-md-10">
                            <TableCSVExport
                                tableInstance={tableInstance}
                                filename={DatabasesLabel(instance.type)}
                                excludeColumns={['breakdown']}
                                instanceTime={instanceTime}
                            />
                        </div>
                        <div className="col col-md-2">
                            <SearchInput
                                ref={inputRef}
                                value={tableFilter}
                                onChange={(e) => setTableFilter(e.target.value)}
                                onClear={clearTableFilter}
                            />
                        </div>
                    </div>
                    <ReactTable
                        getTableProps={getTableProps}
                        getTableBodyProps={getTableBodyProps}
                        headerGroups={headerGroups}
                        prepareRow={prepareRow}
                        page={page}
                        canPreviousPage={canPreviousPage}
                        canNextPage={canNextPage}
                        pageOptions={pageOptions}
                        pageCount={pageCount}
                        gotoPage={gotoPage}
                        nextPage={nextPage}
                        previousPage={previousPage}
                        setPageSize={setPageSize}
                        pageIndex={pageIndex}
                        pageSize={pageSize}
                        dataLength={data.length}
                        error={null}
                        loading={loading === DATA_LOADING}
                    />
                </div>
            </div>
        </div>
    );
}

DatabasesTable.propTypes = {
    setFilterOptions: PropTypes.func.isRequired
};

export default DatabasesTable;
